import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KissStorm from "./KissStorm";

const greetingMaxLength = 40;
const signOffMaxLength = 60;
const wheelSlowdown = 0.28;
const touchSlowdown = 0.38;
const scrollEase = 0.12;
const windowMask = "linear-gradient(180deg, transparent 0%, #000 30%, #000 70%, transparent 100%)";

const splitLetter = (text) => {
  const paragraphs = String(text || "")
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const hasGreeting = paragraphs.length > 1 && paragraphs[0].length <= greetingMaxLength;
  const rest = hasGreeting ? paragraphs.slice(1) : paragraphs;
  const hasSignOff = rest.length > 1 && rest[rest.length - 1].length <= signOffMaxLength;
  return {
    greeting: hasGreeting ? paragraphs[0] : null,
    body: hasSignOff ? rest.slice(0, -1) : rest,
    signOff: hasSignOff ? rest[rest.length - 1] : null,
  };
};

const smoothStep = (edgeStart, edgeEnd, value) => {
  const progress = Math.min(1, Math.max(0, (value - edgeStart) / (edgeEnd - edgeStart)));
  return progress * progress * (3 - 2 * progress);
};

export default function Letter({ text, onReseal, showReseal }) {
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);
  const frameRef = useRef(0);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [isNearEnd, setIsNearEnd] = useState(false);
  const [stormRound, setStormRound] = useState(0);
  const { greeting, body, signOff } = useMemo(() => splitLetter(text), [text]);

  const applyFocus = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const viewHeight = scroller.clientHeight || 1;
    const viewCenter = scroller.scrollTop + viewHeight / 2;
    itemRefs.current.forEach((item) => {
      if (!item) return;
      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const gap = itemTop > viewCenter ? itemTop - viewCenter : itemBottom < viewCenter ? itemBottom - viewCenter : 0;
      const distance = Math.abs(gap) / (viewHeight / 2);
      const focus = 1 - smoothStep(0.08, 0.9, distance);
      const direction = gap > 0 ? 1 : -1;
      const blur = Math.round((1 - focus) * 4) / 2;
      item.style.opacity = (0.04 + 0.96 * focus).toFixed(3);
      item.style.transform = `translate3d(0, ${(direction * (1 - focus) * 16).toFixed(1)}px, 0) scale(${(0.97 + 0.03 * focus).toFixed(3)})`;
      item.style.filter = blur > 0 ? `blur(${blur}px)` : "none";
    });
    const nearEnd = scroller.scrollTop + viewHeight >= scroller.scrollHeight - viewHeight * 0.45;
    setIsNearEnd((current) => (current === nearEnd ? current : nearEnd));
  }, []);

  const scheduleFocus = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(applyFocus);
  }, [applyFocus]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;
    const measure = () => {
      setSpacerHeight(Math.round(scroller.clientHeight * 0.42));
      scheduleFocus();
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer) observer.observe(scroller);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleFocus);
    return () => {
      if (observer) observer.disconnect();
      cancelAnimationFrame(frameRef.current);
    };
  }, [scheduleFocus, text]);

  useEffect(() => {
    scheduleFocus();
  }, [spacerHeight, scheduleFocus]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !showReseal) return undefined;
    let targetTop = scroller.scrollTop;
    let lastTouchY = null;
    let easeFrame = 0;

    const clampTarget = () => {
      targetTop = Math.min(Math.max(0, targetTop), scroller.scrollHeight - scroller.clientHeight);
    };

    const easeTowardTarget = () => {
      const remaining = targetTop - scroller.scrollTop;
      if (Math.abs(remaining) < 0.5) {
        scroller.scrollTop = targetTop;
        easeFrame = 0;
        return;
      }
      scroller.scrollTop += remaining * scrollEase;
      easeFrame = requestAnimationFrame(easeTowardTarget);
    };

    const pushTarget = (distance) => {
      if (!easeFrame) targetTop = scroller.scrollTop;
      targetTop += distance;
      clampTarget();
      if (!easeFrame) easeFrame = requestAnimationFrame(easeTowardTarget);
    };

    const handleWheel = (event) => {
      event.preventDefault();
      const lineHeight = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
      pushTarget(event.deltaY * lineHeight * wheelSlowdown);
    };

    const handleTouchStart = (event) => {
      lastTouchY = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      if (lastTouchY === null) return;
      event.preventDefault();
      const touchY = event.touches[0].clientY;
      pushTarget((lastTouchY - touchY) * touchSlowdown);
      lastTouchY = touchY;
    };

    const handleTouchEnd = () => {
      lastTouchY = null;
    };

    scroller.addEventListener("wheel", handleWheel, { passive: false });
    scroller.addEventListener("touchstart", handleTouchStart, { passive: true });
    scroller.addEventListener("touchmove", handleTouchMove, { passive: false });
    scroller.addEventListener("touchend", handleTouchEnd);
    scroller.addEventListener("touchcancel", handleTouchEnd);
    return () => {
      cancelAnimationFrame(easeFrame);
      scroller.removeEventListener("wheel", handleWheel);
      scroller.removeEventListener("touchstart", handleTouchStart);
      scroller.removeEventListener("touchmove", handleTouchMove);
      scroller.removeEventListener("touchend", handleTouchEnd);
      scroller.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [showReseal]);

  let itemIndex = 0;
  const registerItem = () => {
    const position = itemIndex;
    itemIndex += 1;
    return (element) => {
      itemRefs.current[position] = element;
    };
  };

  return (
    <div style={styles.paper}>
      <div style={styles.frame}>
        <div
          ref={scrollRef}
          className="letter-scroll"
          onScroll={scheduleFocus}
          tabIndex={showReseal ? 0 : -1}
          style={{ ...styles.scroll, overflowY: showReseal ? "auto" : "hidden" }}
        >
          <div style={styles.column}>
            <div style={{ height: spacerHeight }} />
            {greeting && (
              <p ref={registerItem()} style={{ ...styles.item, ...styles.greeting }}>
                {greeting}
              </p>
            )}
            {body.map((paragraph, index) => (
              <p key={index} ref={registerItem()} style={{ ...styles.item, ...styles.body }}>
                {paragraph}
              </p>
            ))}
            {signOff && (
              <p ref={registerItem()} style={{ ...styles.item, ...styles.signOff }}>
                {signOff}
              </p>
            )}
            <div ref={registerItem()} style={{ ...styles.item, ...styles.kissRow }}>
              <button
                type="button"
                className="kiss-button"
                style={styles.kissButton}
                disabled={!showReseal}
                onClick={() => setStormRound((round) => round + 1)}
              >
                click me
              </button>
            </div>
            <div style={{ height: spacerHeight }} />
          </div>
        </div>
        {showReseal && !isNearEnd && (
          <div className="letter-cue" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>
      {showReseal && (
        <div style={styles.footer}>
          <button type="button" onClick={onReseal} style={styles.sealButton}>
            seal it back up
          </button>
        </div>
      )}
      {stormRound > 0 && <KissStorm key={stormRound} onDone={() => setStormRound(0)} />}
    </div>
  );
}

const styles = {
  paper: {
    position: "relative",
    width: "100%",
    height: "100%",
    background: "#fdf8f4",
    borderRadius: 3,
    boxShadow: "0 20px 60px rgba(217,138,163,0.25)",
    padding: "clamp(0.7rem, 3vw, 2rem)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "clamp(0.6rem, 1.6vw, 1rem)",
    minHeight: 0,
  },
  frame: {
    position: "relative",
    flex: 1,
    minHeight: 0,
    display: "flex",
    border: "1px solid #f4b6c2",
    borderRadius: 4,
    overflow: "hidden",
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    padding: "0 clamp(1rem, 4.5vw, 2.6rem)",
    boxSizing: "border-box",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    touchAction: "none",
    outline: "none",
    WebkitMaskImage: windowMask,
    maskImage: windowMask,
  },
  column: {
    maxWidth: "32em",
    margin: "0 auto",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: "clamp(1.22rem, 1.08rem + 0.5vw, 1.5rem)",
    color: "#5a3a44",
  },
  item: {
    willChange: "transform, opacity",
    transformOrigin: "50% 50%",
  },
  greeting: {
    margin: "0 0 0.6em",
    fontFamily: '"Ballet", cursive',
    fontSize: "1.65em",
    lineHeight: 1.5,
    color: "#b86a84",
  },
  body: {
    margin: "0 0 1.1em",
    fontWeight: 500,
    lineHeight: 1.62,
    letterSpacing: "0.005em",
  },
  signOff: {
    margin: "1.2em 0 0",
    fontFamily: '"Ballet", cursive',
    fontSize: "1.45em",
    lineHeight: 1.6,
    color: "#b86a84",
    textAlign: "right",
    whiteSpace: "pre-line",
  },
  kissRow: {
    display: "flex",
    justifyContent: "center",
    padding: "2.4em 0 0",
  },
  kissButton: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: "italic",
    fontSize: "1.15rem",
    color: "#fdf8f4",
    background: "#e0245e",
    border: "none",
    borderRadius: 999,
    minHeight: 46,
    padding: "0 1.8rem",
    cursor: "pointer",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  sealButton: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: "italic",
    fontSize: "1.05rem",
    color: "#fdf8f4",
    background: "#d98aa3",
    border: "none",
    borderRadius: "999px",
    minHeight: 42,
    padding: "0 1.3rem",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(217,138,163,0.3)",
  },
};
